/**
 * Phase 5c — Lots lesen: entity_* (NO_KV_BLOB) + Warm-Cache + Legacy-Blob-Recovery.
 */

import { listAggregateLots } from '../infrastructure/storage/domainAggregateStore';
import { listEntityLots } from '../infrastructure/storage/entityAggregateStore';
import { kvGet, kvRemove } from '../infrastructure/storage/kvAccess';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { readBlobFirstWithAggregateFallback } from './canonicalEntityRead';
import {
  normalizeLotsField,
  readSyncWithBlobPriority,
  readArrayFieldFromMergedBlob,
  scheduleEntityFieldHydrate
} from './businessStateSync';
import { registerEntityFieldHydrator } from './entityFieldHydratorsRegistry';
import { normalizeBusinessStorageId } from './sharedCore';

/** @type {Map<string, object[]>} */
const lotsReadCache = new Map();
/**
 * Sticky last-good snapshot — survives clearLotsReadCache so sync readers
 * under NO_KV_BLOB never silently see [] while entity_lots still has data.
 * @type {Map<string, object[]>}
 */
const lotsLastGoodCache = new Map();

function lotsCacheKey(businessId) {
  if (!businessId) return null;
  return String(normalizeBusinessStorageId(businessId) || businessId);
}

function lotFreshnessMs(lot) {
  const t = Date.parse(lot?.updatedAt || lot?.createdAt || 0);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Union by lot id — keep the fresher row (updatedAt/createdAt).
 * Prevents in-flight entity reloads from wiping optimistic warm seeds
 * after updateLotAttributes / receiveGoods.
 */
export function mergeLotsPreferFresher(primary = [], secondary = []) {
  const byId = new Map();
  for (const lot of [...(Array.isArray(secondary) ? secondary : []), ...(Array.isArray(primary) ? primary : [])]) {
    if (!lot || lot.id == null) continue;
    const key = String(lot.id);
    const prev = byId.get(key);
    if (!prev || lotFreshnessMs(lot) >= lotFreshnessMs(prev)) {
      byId.set(key, lot);
    }
  }
  return [...byId.values()];
}

export function readLotsFromMergedBlob(businessId) {
  return readArrayFieldFromMergedBlob(
    businessId,
    'lots',
    getCachedLotsRead,
    normalizeLotsField
  );
}

/**
 * @param {string} businessId
 * @returns {Promise<object[]>}
 */
export async function readLotsFromBestSource(businessId) {
  return readBlobFirstWithAggregateFallback(
    businessId,
    readLotsFromMergedBlob,
    listAggregateLots,
    listEntityLots,
    ['id']
  );
}

/**
 * NO_KV_BLOB: sync readers only see warm cache. If cache is cold/empty while
 * entity_lots has data, hydrate cache from entity (async) so receiveGoods /
 * restock / migration do not treat inventory as empty.
 * @param {string} businessId
 * @returns {Promise<object[]>}
 */
export async function ensureLotsLoadedForBusiness(businessId) {
  if (!businessId) return [];
  const cached = getCachedLotsRead(businessId);
  if (Array.isArray(cached) && cached.length > 0) {
    return cached;
  }
  const lots = await readLotsFromBestSource(businessId);
  seedLotsReadCache(businessId, lots);
  return Array.isArray(lots) ? lots : [];
}

// Warm-cache miss under NO_KV_BLOB → reload entity_lots (not silent [])
registerEntityFieldHydrator('lots', ensureLotsLoadedForBusiness);

export async function refreshLotsReadCache(businessId, preloaded) {
  if (preloaded !== undefined) {
    const lots = Array.isArray(preloaded) ? preloaded : [];
    seedLotsReadCache(businessId, lots);
    return lots;
  }

  const warm = getCachedLotsRead(businessId);
  const fromSource = await readLotsFromBestSource(businessId);
  const sourceList = Array.isArray(fromSource) ? fromSource : [];
  // Prefer fresher warm rows while entity write may still be catching up
  const lots =
    Array.isArray(warm) && warm.length > 0
      ? mergeLotsPreferFresher(warm, sourceList)
      : sourceList;
  seedLotsReadCache(businessId, lots);
  return lots;
}

export function getCachedLotsRead(businessId) {
  const key = lotsCacheKey(businessId);
  if (!key) return null;
  const hit = lotsReadCache.get(key);
  if (hit) return hit;
  const raw = String(businessId);
  return lotsReadCache.get(raw) ?? lotsReadCache.get(`business_${key}`) ?? null;
}

/**
 * Synchroner Lesepfad.
 * Under NO_KV_BLOB: warm cache → sticky last-good → schedule entity hydrate.
 * Never prefer a silent empty array when we still know prior lots.
 */
export function readLotsSyncForBusiness(businessId) {
  if (!businessId) return [];

  const fromPriority = readSyncWithBlobPriority(
    businessId,
    readLotsFromMergedBlob,
    getCachedLotsRead,
    'id'
  );
  if (Array.isArray(fromPriority) && fromPriority.length > 0) {
    return fromPriority;
  }

  if (!isEntityStoreNoKvBlobEnabled()) {
    return fromPriority;
  }

  const key = lotsCacheKey(businessId);
  const sticky = key ? lotsLastGoodCache.get(key) : null;
  // Cold miss: kick entity_* reload (async) so the next sync/async read is warm
  scheduleEntityFieldHydrate('lots', businessId);

  if (Array.isArray(sticky) && sticky.length > 0) {
    return sticky;
  }
  return fromPriority ?? [];
}

export function clearLotsReadCache(businessId) {
  if (businessId) {
    const key = lotsCacheKey(businessId);
    if (key) {
      lotsReadCache.delete(key);
      lotsReadCache.delete(String(businessId));
      lotsReadCache.delete(`business_${key}`);
      // Keep lotsLastGoodCache — only cleared on explicit business wipe / empty seed
    }
  } else {
    lotsReadCache.clear();
    lotsLastGoodCache.clear();
  }
}

export function seedLotsReadCache(businessId, lots) {
  const key = lotsCacheKey(businessId);
  if (!key || lots === undefined) return;
  const normalized = Array.isArray(lots) ? lots : normalizeLotsField(lots);
  lotsReadCache.set(key, normalized);
  if (normalized.length > 0) {
    lotsLastGoodCache.set(key, normalized);
  } else {
    // Authoritative empty (rebuild wiped lots) — drop sticky
    lotsLastGoodCache.delete(key);
  }
}

/**
 * Entfernt den Legacy-Standalone-Key `lots` (P0), sobald der Blob Lots enthält.
 * @param {string} businessId
 * @returns {boolean} true wenn der Legacy-Key entfernt wurde
 */
export function removeLegacyStandaloneLotsKey(businessId) {
  if (!businessId) return false;

  const blobLots = readLotsSyncForBusiness(businessId);
  if (!Array.isArray(blobLots) || blobLots.length === 0) {
    return false;
  }

  const legacy = kvGet('lots');
  if (legacy == null) {
    return false;
  }

  kvRemove('lots');
  console.warn('[MIGRATION] Removed legacy lots key');
  return true;
}

